import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { TrendingUp, TrendingDown, ArrowRight, Plus, Eye, Users, Trash2, Search, Filter, Calendar, List, BookOpen, Mail, MoreHorizontal, ChevronDown, Copy, Archive, Edit, FileText, Tag, RefreshCw, Target, BarChart3, Zap } from 'lucide-react'
import { useCampaigns, Campaign } from '@/hooks/useCampaigns'
import { useClients } from '@/hooks/useClients'
import { useWorkspaceClient } from '@/hooks/useWorkspaceClient'
import { useNavigate } from 'react-router-dom'
import { useCampaignStatsTracker } from '@/hooks/useCampaignStatsTracker'
// Dynamic import to avoid circular dependency initialization issues
// import { CampaignPerformanceCache } from '@/lib/campaign-performance-cache'
import { useOptimizedCampaignPerformance } from '@/hooks/useOptimizedCampaignPerformance'
import { CreateCampaignDialog } from '@/components/campaigns/CreateCampaignDialog'
import { RenameCampaignDialog } from '@/components/campaigns/RenameCampaignDialog'
import { AddTagsDialog } from '@/components/campaigns/AddTagsDialog'
import { EditCampaignDialog } from '@/components/campaigns/EditCampaignDialog'
import { CampaignCalendarView } from './CampaignCalendarView'
import { SegmentedCampaignPerformance } from './SegmentedCampaignPerformance'
import { useDashboard } from '@/contexts/DashboardContext'

import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'

interface CampaignPerformanceData {
  id: string
  name: string
  status: 'Active' | 'Paused' | 'Completed'
  sent: number
  replies: number
  replyRate: number
  positiveReplies: number
  positiveRate: number
  meetings: number
  meetingRate: number
  trend: 'up' | 'down' | 'stable'
}

// Campaign Metrics Logic Hook
function useCampaignMetrics(selectedClientId?: string) {
  const [totalCampaigns, setTotalCampaigns] = useState(0)
  const [campaignsLive, setCampaignsLive] = useState(0)
  const [triggersLive, setTriggersLive] = useState(0)
  const [activeVariants, setActiveVariants] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function calculateMetrics() {
      try {
        setLoading(true)

        // Query campaigns table directly — no SmartLead API needed
        let allQuery = supabase
          .from('campaigns')
          .select('id, status, campaign_type')
          .neq('status', 'archived')

        if (selectedClientId && selectedClientId !== 'all') {
          allQuery = allQuery.eq('client_id', selectedClientId)
        }

        const { data: allCampaigns, error } = await allQuery
        if (error || !allCampaigns) {
          console.error('Error fetching campaigns for metrics:', error)
          return
        }

        const active = allCampaigns.filter(c => c.status === 'active')

        setTotalCampaigns(allCampaigns.length)
        setCampaignsLive(active.filter(c => c.campaign_type !== 'triggers').length)
        setTriggersLive(active.filter(c => c.campaign_type === 'triggers').length)

        // Active variants: count from ab_test_variants for these campaigns
        const campaignIds = active.map(c => c.id)
        if (campaignIds.length > 0) {
          const { count } = await supabase
            .from('ab_test_variants')
            .select('id', { count: 'exact', head: true })
            .eq('status', 'active')
            .in('segment_id',
              // Get segment IDs for these campaigns
              (await supabase
                .from('ab_test_segments')
                .select('id')
                .in('campaign_id', campaignIds)
              ).data?.map(s => s.id) || []
            )
          setActiveVariants(count || 0)
        } else {
          setActiveVariants(0)
        }

      } catch (error) {
        console.error('Campaign metrics calculation error:', error)
      } finally {
        setLoading(false)
      }
    }

    calculateMetrics()
  }, [selectedClientId])

  return { totalCampaigns, campaignsLive, triggersLive, activeVariants, loading }
}


export function CampaignPerformanceTest() {
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  
  // Removed excessive debug logging to prevent console spam
  const [showRenameDialog, setShowRenameDialog] = useState(false)
  const [showTagsDialog, setShowTagsDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [renameSelectedCampaign, setRenameSelectedCampaign] = useState<Campaign | null>(null)
  const [tagsSelectedCampaign, setTagsSelectedCampaign] = useState<Campaign | null>(null)
  const [editSelectedCampaign, setEditSelectedCampaign] = useState<Campaign | null>(null)
  const [currentView, setCurrentView] = useState<'list' | 'calendar'>('list')
  const [campaignTags, setCampaignTags] = useState<{[key: string]: string[]}>({})
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('active')
  const [metricFilter, setMetricFilter] = useState<'all' | 'campaigns' | 'triggers'>('all')
  const { state, setSelectedClient } = useDashboard()
  const selectedClientId = state.selectedClient
  
  // Campaign Metrics Logic
  const { totalCampaigns, campaignsLive, triggersLive, activeVariants, loading: metricsLoading } = useCampaignMetrics(selectedClientId)

  const workspace = useWorkspaceClient()

  // Load performance data from the same materialized view used by Campaigns Live (unified source)
  const { campaigns: viewCampaigns, loading: viewLoading } = useOptimizedCampaignPerformance(
    workspace.isClientUser ? workspace.client?.id : selectedClientId,
    'all'
  )
  // Pass null while workspace is loading for client users to prevent unfiltered data leakage
  const campaignFilter = workspace.isClientUser && workspace.loading ? null : workspace.campaignClientId
  const { campaigns, fetchCampaigns, deleteCampaign, loading: campaignsLoading } = useCampaigns(true, campaignFilter)
  const { data: allClients = [] } = useClients()
  
  // Filter out the system "default" client used for global API keys and any clients with empty IDs
  // MEMOIZED: Prevent new array creation on every render to avoid infinite re-renders
  const clients = useMemo(() => 
    allClients.filter(client => client.id !== 'default' && client.id && client.id.trim() !== ''),
    [allClients]
  )
  
  // Helper function to get client name - MEMOIZED to prevent recreation on every render
  const getClientName = useCallback((clientId: string) => {
    const client = clients.find(c => c.id === clientId)
    return client?.name || 'Unknown Client'
  }, [clients])
  
  // Filter campaigns by selected client, search term, status, and channel
  // OPTIMIZED: Removed clients from dependencies - getClientName is now memoized and stable
  const filteredCampaigns = useMemo(() => {
    let filtered = selectedClientId === 'all' 
      ? campaigns 
      : campaigns.filter(campaign => campaign.client_id === selectedClientId)
    
    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(campaign => 
        campaign.name.toLowerCase().includes(searchLower) ||
        campaign.description?.toLowerCase().includes(searchLower) ||
        getClientName(campaign.client_id).toLowerCase().includes(searchLower)
      )
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(campaign => {
        // Normalize both status values for comparison
        const campaignStatus = campaign.status?.toLowerCase()
        const filterStatus = statusFilter.toLowerCase()
        return campaignStatus === filterStatus
      })
    }
    
    // Apply metric filter (campaigns vs triggers)
    if (metricFilter !== 'all') {
      filtered = filtered.filter(campaign => {
        if (metricFilter === 'campaigns') {
          return campaign.campaign_type !== 'triggers'
        } else if (metricFilter === 'triggers') {
          return campaign.campaign_type === 'triggers'
        }
        return true
      })
    }
    
    return filtered
  }, [campaigns, selectedClientId, searchTerm, statusFilter, metricFilter, getClientName])
  
  // Performance data derived from the same materialized view as Campaigns Live (unified source)
  const performanceData = useMemo(() => {
    const data: Record<string, {
      leadsContacted: number
      sendVolume: number
      replies: number
      humanReplies: number
      positiveReplies: number
      replyRate: number
      humanReplyRate: number
      positiveRate: number
      meetingsBooked: number
      dealsWon: number
      revenueGenerated: number
      loading?: boolean
      lastUpdated?: string
    }> = {}
    viewCampaigns.forEach(camp => {
      data[camp.campaign_id] = {
        leadsContacted: Number(camp.email_leads_contacted) || 0,
        sendVolume: Number(camp.email_send_volume) || 0,
        replies: Number(camp.email_total_replies) || 0,
        humanReplies: Number(camp.email_human_replies) || 0,
        positiveReplies: Number(camp.email_positive_replies) || 0,
        replyRate: Number(camp.email_reply_rate) || 0,
        humanReplyRate: Number(camp.email_human_reply_rate) || 0,
        positiveRate: Number(camp.email_positive_rate) || 0,
        meetingsBooked: Number(camp.meetings_booked) || 0,
        dealsWon: Number(camp.deals_won) || 0,
        revenueGenerated: Number(camp.deals_won) * 15000,
        loading: false,
      }
    })
    return data
  }, [viewCampaigns])
  
  const performanceLoading = viewLoading

  // Performance data now comes from the materialized view via useOptimizedCampaignPerformance
  // No separate fetch, no localStorage cache, no UnifiedCampaignPerformance lib — single source of truth
  
  // Ensure all filtered campaigns have entries (with zeros if no cache and not yet fetched)
  const completePerformanceData = useMemo(() => {
    try {
      if (!performanceData) return {}
      const data = { ...performanceData }
      if (filteredCampaigns && Array.isArray(filteredCampaigns) && filteredCampaigns.length > 0) {
        filteredCampaigns.forEach(campaign => {
          try {
            if (campaign && campaign.id && !data[campaign.id]) {
              data[campaign.id] = {
                leadsContacted: 0,
                sendVolume: 0,
                replies: 0,
                humanReplies: 0,
                positiveReplies: 0,
                replyRate: 0,
                humanReplyRate: 0,
                positiveRate: 0,
                meetingsBooked: 0,
                dealsWon: 0,
                revenueGenerated: 0,
                loading: false
              }
            }
          } catch (error) {
            console.error('Error processing campaign in completePerformanceData:', campaign?.id, error)
          }
        })
      }
      return data
    } catch (error) {
      console.error('Error in completePerformanceData useMemo:', error)
      return performanceData || {}
    }
  }, [performanceData, filteredCampaigns])
  
  // Refresh function
  const refreshPerformance = useCallback(async () => {
    // Clear cache and force fresh fetch
    const { CampaignPerformanceCache } = await import('@/lib/campaign-performance-cache')
    CampaignPerformanceCache.clearCache() // clearCache() without args clears all cache
    setPerformanceData({})
    // Trigger re-fetch by updating a dependency
    const campaignIds = filteredCampaigns.map(c => c.id).join(',')
    // Force re-render to trigger useEffect
    window.dispatchEvent(new CustomEvent('forceRefreshPerformance'))
  }, [filteredCampaigns])
  
  // Removed excessive debug logging to prevent console spam
  const { toast } = useToast()
  const navigate = useNavigate()

  // DISABLED: Campaign tags loading that might cause freezing
  const loadAllCampaignTags = useCallback(async () => {
    // Campaign tags loading disabled to prevent freezing
    // if (filteredCampaigns.length === 0) return
    
    // const tagsData: {[key: string]: string[]} = {}
    // for (const campaign of filteredCampaigns) {
    //   const tags = await getCampaignTags(campaign.id)
    //   tagsData[campaign.id] = tags
    // }
    // setCampaignTags(tagsData)
  }, [])

  const handleRefreshCampaigns = useCallback(() => {
    fetchCampaigns()
    refreshPerformance()
    loadAllCampaignTags()
  }, []) // Remove dependencies to prevent infinite loops

  // Auto-track campaign stats when new campaigns/variants are added - DISABLED to prevent infinite loops
  const { triggerStatsUpdate } = useCampaignStatsTracker({
    onStatsUpdate: handleRefreshCampaigns,
    enableAutoRefresh: false // DISABLED to prevent continuous refreshing
  })

  useEffect(() => {
    loadAllCampaignTags()
  }, []) // Remove dependency to prevent infinite loops

  // FIXED: Only run initial load once, not on every render - DISABLED to prevent infinite loops
  useEffect(() => {
    // Initial campaign load
    // DISABLED: handleRefreshCampaigns() - This was causing infinite loops
    // The campaigns will be loaded by the useCampaigns hook automatically
  }, []) // Empty dependency array - only run once on mount

  // Refetch campaigns when status filter changes
  useEffect(() => {
    fetchCampaigns()
  }, [statusFilter]) // Remove fetchCampaigns dependency

  // DISABLED: Set up periodic refresh and cache update listener - CAUSING INFINITE LOOPS
  useEffect(() => {
    // DISABLED: All cache update listeners to prevent infinite refresh cycles
    // The performance data will be loaded by the useOptimizedCampaignPerformance hook (materialized view)
    
    return () => {
      // Clean up any existing listeners
      import('@/lib/campaign-performance-cache').then(({ CampaignPerformanceCache }) => {
        CampaignPerformanceCache.stopPeriodicRefresh()
      }).catch(() => {
        // Ignore errors in cleanup
      })
    }
  }, []) // Empty dependency array - no automatic refresh

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'Active':
      case 'active':
        return 'default'
      case 'Paused':
      case 'paused':
        return 'secondary'
      case 'Completed':
      case 'completed':
        return 'outline'
      default:
        return 'secondary'
    }
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="h-3 w-3 text-green-500" />
      case 'down':
        return <TrendingDown className="h-3 w-3 text-red-500" />
      default:
        return <ArrowRight className="h-3 w-3 text-muted-foreground" />
    }
  }

  const handleDeleteCampaign = async (campaignId: string, campaignName: string) => {
    if (confirm(`Are you sure you want to delete campaign "${campaignName}"? This action cannot be undone.`)) {
      try {
        await deleteCampaign(campaignId)
        await handleRefreshCampaigns()
      } catch (error) {
        console.error('Error deleting campaign:', error)
        alert('Failed to delete campaign. Please try again.')
      }
    }
  }

  const formatNumber = (num: number) => {
    return num.toLocaleString()
  }

  const formatPercentage = (num: number) => {
    return `${num.toFixed(1)}%`
  }


  const getCampaignObjective = (campaignName: string) => {
    const name = campaignName.toLowerCase()
    if (name.includes('meeting') || name.includes('book') || name.includes('demo')) {
      return { label: 'Meeting Booking', color: 'bg-blue-50 text-blue-700 border-blue-200' }
    } else if (name.includes('engagement') || name.includes('nurture') || name.includes('awareness')) {
      return { label: 'Engagement', color: 'bg-purple-50 text-purple-700 border-purple-200' }
    } else if (name.includes('lead') || name.includes('generation') || name.includes('prospect')) {
      return { label: 'Lead Generation', color: 'bg-green-50 text-green-700 border-green-200' }
    } else if (name.includes('launch') || name.includes('product') || name.includes('announcement')) {
      return { label: 'Product Launch', color: 'bg-orange-50 text-orange-700 border-orange-200' }
    } else if (name.includes('brand') || name.includes('thought') || name.includes('content')) {
      return { label: 'Brand Awareness', color: 'bg-pink-50 text-pink-700 border-pink-200' }
    } else {
      return { label: 'Outreach', color: 'bg-gray-50 text-gray-700 border-gray-200' }
    }
  }

  // Campaign Type categorization based on campaign name patterns
  const getCampaignType = (campaignName: string) => {
    const name = campaignName.toLowerCase()
    
    if (name.includes('bulk') || name.includes('mass') || name.includes('batch')) {
      return { label: 'Bulk Campaigns', color: 'bg-blue-50 text-blue-700 border-blue-200' }
    }
    if (name.includes('trigger') || name.includes('automated') || name.includes('workflow')) {
      return { label: 'Triggers', color: 'bg-purple-50 text-purple-700 border-purple-200' }
    }
    if (name.includes('adhoc') || name.includes('ad-hoc') || name.includes('one-off') || name.includes('custom')) {
      return { label: 'Adhoc', color: 'bg-orange-50 text-orange-700 border-orange-200' }
    }
    if (name.includes('event') || name.includes('conference') || name.includes('webinar') || name.includes('summit')) {
      return { label: 'Events', color: 'bg-green-50 text-green-700 border-green-200' }
    }
    
    // Default based on campaign characteristics
    return { label: 'Bulk Campaigns', color: 'bg-blue-50 text-blue-700 border-blue-200' }
  }

  const handleCampaignClick = (campaign: Campaign, channel?: 'email' | 'linkedin') => {
    if (channel === 'linkedin') {
      navigate(`/campaign-performance-test/campaign/${campaign.id}?tab=ab-test&channel=linkedin`)
    } else {
      navigate(`/campaign-performance-test/campaign/${campaign.id}`)
    }
  }

  const handleAddToTemplates = (campaign: Campaign) => {
    toast({
      title: "Added to Templates",
      description: `Campaign "${campaign.name}" has been added to your template library.`,
    })
  }

  const handleCloneCampaign = async (campaign: Campaign) => {
    try {
      console.log('Cloning campaign:', campaign)
      
      // Create a new campaign object with the same details but new name and ID
      const clonedCampaign = {
        name: `${campaign.name} - Copy`,
        client_id: campaign.client_id,
        description: campaign.description,
        status: 'active', // Start cloned campaigns as active
        campaign_type: campaign.campaign_type,
        trigger_type: campaign.trigger_type,
        channels: campaign.channels || [],
        version: campaign.version || '1.0',
        target_regions: campaign.target_regions || [],
        clay_table_link: campaign.clay_table_link,
        clickup_task_link: campaign.clickup_task_link,
        copywriting_doc_link: campaign.copywriting_doc_link,
        tam_list_file_name: campaign.tam_list_file_name,
        tam_list_file_url: campaign.tam_list_file_url
      }
      
      const { data, error } = await supabase
        .from('campaigns')
        .insert(clonedCampaign)
        .select()
      
      if (error) {
        console.error('Error cloning campaign:', error)
        toast({
          title: "Error",
          description: `Failed to clone campaign: ${error.message}`,
          variant: "destructive"
        })
        return
      }

      console.log('Campaign cloned successfully:', data)
      toast({
        title: "Campaign Cloned",
        description: `A copy of "${campaign.name}" has been created as "${clonedCampaign.name}".`,
      })
      handleRefreshCampaigns()
    } catch (error) {
      console.error('Unexpected error:', error)
      toast({
        title: "Error",
        description: "An unexpected error occurred while cloning the campaign.",
        variant: "destructive"
      })
    }
  }

  const handleCopyCampaignId = (campaign: Campaign) => {
    navigator.clipboard.writeText(campaign.id)
    toast({
      title: "Campaign ID Copied",
      description: "Campaign ID has been copied to clipboard.",
    })
  }

  const handleEditDetails = (campaign: Campaign) => {
    console.log('Opening edit dialog for campaign:', campaign)
    setEditSelectedCampaign(campaign)
    setShowEditDialog(true)
  }

  const handleMoveToArchive = async (campaign: Campaign) => {
    try {
      console.log('Archiving campaign:', campaign.id)
      
      const { data, error } = await supabase
        .from('campaigns')
        .update({ status: 'archived' })
        .eq('id', campaign.id)
        .select()
      
      if (error) {
        console.error('Error archiving campaign:', error)
        toast({
          title: "Error",
          description: `Failed to archive campaign: ${error.message}`,
          variant: "destructive"
        })
        return
      }

      console.log('Campaign archived successfully:', data)
      toast({
        title: "Moved to Archive",
        description: `Campaign "${campaign.name}" has been archived.`,
      })
      handleRefreshCampaigns()
    } catch (error) {
      console.error('Unexpected error:', error)
      toast({
        title: "Error",
        description: "An unexpected error occurred while archiving the campaign.",
        variant: "destructive"
      })
    }
  }

  const handleRenameCampaign = (campaign: Campaign) => {
    console.log('Setting rename campaign:', campaign)
    setRenameSelectedCampaign(campaign)
    setShowRenameDialog(true)
  }

  const handleAddTags = (campaign: Campaign) => {
    setTagsSelectedCampaign(campaign)
    setShowTagsDialog(true)
  }

  const handleRename = async (campaignId: string, newName: string) => {
    try {
      console.log('Updating campaign:', { campaignId, newName })
      
      const { data, error } = await supabase
        .from('campaigns')
        .update({ name: newName })
        .eq('id', campaignId)
        .select()
      
      if (error) {
        console.error('Error updating campaign:', error)
        toast({
          title: "Error",
          description: `Failed to rename campaign: ${error.message}`,
          variant: "destructive"
        })
        return
      }

      console.log('Campaign updated successfully:', data)
      toast({
        title: "Campaign Renamed",
        description: `Campaign has been renamed to "${newName}".`,
      })
      handleRefreshCampaigns()
    } catch (error) {
      console.error('Unexpected error:', error)
      toast({
        title: "Error",
        description: "An unexpected error occurred while renaming the campaign.",
        variant: "destructive"
      })
    }
  }

  const handleUpdateTags = async (campaignId: string, tags: string[]) => {
    try {
      console.log('Updating campaign tags:', { campaignId, tags })
      
      // Tags functionality disabled - no campaign_tags table exists
      console.log('Campaign tags update skipped - feature not implemented')

      console.log('Campaign tags updated successfully')
      toast({
        title: "Tags Updated",
        description: `Campaign tags have been updated.`,
      })
      handleRefreshCampaigns()
    } catch (error) {
      console.error('Unexpected error:', error)
      toast({
        title: "Error",
        description: "An unexpected error occurred while updating tags.",
        variant: "destructive"
      })
    }
  }

  const getCampaignTags = async (campaignId: string) => {
    // Tags functionality disabled - no campaign_tags table exists
    return []
  }

  const getTagColor = (tag: string) => {
    const colors = [
      'bg-blue-50 text-blue-700 border-blue-200',
      'bg-green-50 text-green-700 border-green-200', 
      'bg-purple-50 text-purple-700 border-purple-200',
      'bg-orange-50 text-orange-700 border-orange-200',
      'bg-pink-50 text-pink-700 border-pink-200',
      'bg-indigo-50 text-indigo-700 border-indigo-200',
      'bg-red-50 text-red-700 border-red-200',
      'bg-yellow-50 text-yellow-700 border-yellow-200'
    ]
    
    // Use tag name to consistently assign colors
    const hash = tag.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0)
      return a & a
    }, 0)
    
    return colors[Math.abs(hash) % colors.length]
  }

  const handleUpdateCampaign = async (campaignId: string, updates: Partial<Campaign>) => {
    try {
      console.log('Updating campaign details:', { campaignId, updates })
      
      const { data, error } = await supabase
        .from('campaigns')
        .update(updates)
        .eq('id', campaignId)
        .select()
      
      if (error) {
        console.error('Error updating campaign:', error)
        toast({
          title: "Error",
          description: `Failed to update campaign: ${error.message}`,
          variant: "destructive"
        })
        return
      }

      console.log('Campaign updated successfully:', data)
      toast({
        title: "Campaign Updated",
        description: "Campaign details have been updated successfully.",
      })
      handleRefreshCampaigns()
    } catch (error) {
      console.error('Unexpected error:', error)
      toast({
        title: "Error",
        description: "An unexpected error occurred while updating the campaign.",
        variant: "destructive"
      })
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold text-foreground">Campaign Performance</h1>
          <p className="text-sm text-muted-foreground">Campaign performance analysis and metrics</p>
          {performanceLoading && (
            <Badge variant="secondary" className="text-xs mt-1 w-fit">
              Loading performance data...
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleRefreshCampaigns}
            disabled={campaignsLoading || performanceLoading}
            title="Refresh all data and clear cache"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${(campaignsLoading || performanceLoading) ? 'animate-spin' : ''}`} />
            {(campaignsLoading || performanceLoading) ? 'Updating...' : 'Refresh'}
          </Button>
          <Button 
            variant={currentView === 'list' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setCurrentView('list')}
          >
            <List className="h-4 w-4 mr-2" />
            List
          </Button>
          {/* Create campaign — admin only */}
          {!workspace.isClientUser && (
          <Button
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setShowCreateDialog(true)
            }}
            className="bg-foreground text-background hover:bg-foreground/90"
            disabled={false}
            type="button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create campaign
          </Button>
          )}
        </div>
      </div>

      {/* Section Title */}
      <div className="mt-4">
        <h2 className="text-xl font-semibold text-foreground">Campaigns</h2>
      </div>

      {/* Campaign Metrics */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-5">
        <div className="bg-card border rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total</p>
              <p className="text-2xl font-bold">
                {metricsLoading ? '...' : totalCampaigns}
              </p>
            </div>
            <List className="h-8 w-8 text-muted-foreground" />
          </div>
        </div>
        
        <div 
          className={`bg-card border rounded-lg p-6 cursor-pointer transition-all hover:shadow-md ${
            metricFilter === 'campaigns' ? 'ring-2 ring-blue-500 bg-blue-50' : ''
          }`}
          onClick={() => {
            if (metricFilter === 'campaigns') {
              setMetricFilter('all')
            } else {
              setMetricFilter('campaigns')
            }
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Campaigns Live</p>
              <p className="text-2xl font-bold">
                {metricsLoading ? '...' : campaignsLive}
              </p>
            </div>
            <Target className="h-8 w-8 text-muted-foreground" />
          </div>
        </div>
        
        <div 
          className={`bg-card border rounded-lg p-6 cursor-pointer transition-all hover:shadow-md ${
            metricFilter === 'triggers' ? 'ring-2 ring-amber-500 bg-amber-50' : ''
          }`}
          onClick={() => {
            if (metricFilter === 'triggers') {
              setMetricFilter('all')
            } else {
              setMetricFilter('triggers')
            }
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Triggers Live</p>
              <p className="text-2xl font-bold">
                {metricsLoading ? '...' : triggersLive}
              </p>
            </div>
            <Zap className="h-8 w-8 text-muted-foreground" />
          </div>
        </div>
        
        <div className="bg-card border rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Active Variants</p>
              <p className="text-2xl font-bold">
                {metricsLoading ? '...' : activeVariants}
              </p>
            </div>
            <BarChart3 className="h-8 w-8 text-muted-foreground" />
          </div>
        </div>
        
        <div className="bg-card border rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Active Rate</p>
              <p className="text-2xl font-bold">
                {metricsLoading ? '...' : 
                  totalCampaigns > 0 ? 
                    `${Math.round(((campaignsLive + triggersLive) / totalCampaigns) * 100)}%` : 
                    '0%'
                }
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-muted-foreground" />
          </div>
        </div>
      </div>

      {/* Active Filters Display — hidden for client users since they only see their own data */}
      {!workspace.isClientUser && (metricFilter !== 'all' || selectedClientId !== 'all') && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <span className="text-sm font-medium text-blue-800">Active Filters:</span>
          {selectedClientId !== 'all' && (
            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md">
              Client: {getClientName(selectedClientId)}
            </span>
          )}
          {metricFilter === 'campaigns' && (
            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-md">
              Campaigns Only
            </span>
          )}
          {metricFilter === 'triggers' && (
            <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded-md">
              Triggers Only
            </span>
          )}
          <button
            onClick={() => {
              setMetricFilter('all')
              setSelectedClient('all')
            }}
            className="text-xs text-blue-600 hover:text-blue-800 underline"
          >
            Clear All
          </button>
        </div>
      )}

      {/* Filter Bar */}
      <div className="flex items-center gap-4 py-4 border-y border-dashed border-border">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search campaigns..." 
            className="pl-10 border-dashed"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>


        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-32 border-dashed">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-background border shadow-md z-50">
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="ramping">Ramping</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>

        {/* Client selector — hidden for client users since they only see their own data */}
        {!workspace.isClientUser && (
        <Select value={selectedClientId} onValueChange={setSelectedClient}>
          <SelectTrigger className="w-40 border-dashed">
            <SelectValue placeholder="All Clients" />
          </SelectTrigger>
          <SelectContent className="bg-background border shadow-md z-50">
            <SelectItem value="all">All Clients</SelectItem>
            {clients.map((client) => {
              // Extra safety check to ensure no empty strings
              if (!client.id || client.id.toString().trim() === '') {
                return null
              }
              return (
                <SelectItem key={client.id} value={client.id.toString()}>
                  {client.name}
                </SelectItem>
              )
            }).filter(Boolean)}
          </SelectContent>
        </Select>
        )}
      </div>

      {/* Content - List and Calendar Views - Always render both to maintain hook consistency */}
      
      {/* List View */}
      <div className={currentView === 'list' ? 'block' : 'hidden'}>
        {/* Segmented Performance View - Always render but conditionally show content */}
        <SegmentedCampaignPerformance
          campaigns={filteredCampaigns}
          clients={clients}
          performanceData={completePerformanceData} // HYBRID: Pass cached data (instant) + fresh data (accurate)
          onCampaignClick={handleCampaignClick}
          onEditDetails={handleEditDetails}
          onRenameCampaign={handleRenameCampaign}
          onCloneCampaign={handleCloneCampaign}
          onMoveToArchive={handleMoveToArchive}
        />

        {filteredCampaigns.length === 0 && !campaignsLoading && (
          <div className="text-center py-12 border border-border rounded-lg">
            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No campaigns yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first campaign to get started with outbound management
            </p>
            <Button onClick={() => setShowCreateDialog(true)} className="bg-foreground text-background hover:bg-foreground/90">
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Campaign
            </Button>
          </div>
        )}
      </div>
      
      {/* Calendar View */}
      <div className={currentView === 'calendar' ? 'block' : 'hidden'}>
        <CampaignCalendarView 
          campaigns={filteredCampaigns}
          onCampaignClick={handleCampaignClick}
        />
      </div>

      <CreateCampaignDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSuccess={handleRefreshCampaigns}
      />

      <RenameCampaignDialog
        campaign={renameSelectedCampaign}
        open={showRenameDialog}
        onOpenChange={setShowRenameDialog}
        onRename={handleRename}
      />

      <AddTagsDialog
        campaign={tagsSelectedCampaign}
        open={showTagsDialog}
        onOpenChange={setShowTagsDialog}
        onUpdateTags={handleUpdateTags}
      />

      <EditCampaignDialog
        key={`edit-${editSelectedCampaign?.id || 'none'}`}
        campaign={editSelectedCampaign}
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        onUpdate={handleUpdateCampaign}
        allCampaigns={campaigns}
      />

    </div>
  )
}